import type { FileError, FileRejection, FileWithPath } from 'react-dropzone';

import { type Values } from '../../../../utils/types';

export const FILE_TYPE = {
  document: 'document',
  image: 'image',
  template: 'template',
  video: 'video',
} as const;

export type FileData = {
  errors?: readonly FileError[];
  readonly file: File; // original one
  name: string;
  src: any; // blob | ArrayBuffer. Any to avoid conflicts on img and video html tags
  type: Values<typeof FILE_TYPE>;
};

export async function getFilesData(files: FileWithPath[] | FileRejection[]): Promise<FileData[]> {
  const promises = files.map((item) => {
    const file = 'file' in item ? item.file : item;
    const errors = 'errors' in item ? item.errors : undefined;

    const fileType = file.type.split('/')[0];
    let type: Values<typeof FILE_TYPE> = FILE_TYPE.document;
    if (fileType === FILE_TYPE.image || fileType === FILE_TYPE.video) {
      type = fileType;
    }
    if (file.type === 'text/html') {
      type = FILE_TYPE.template;
    }

    return new Promise<FileData>((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = (e) => {
        resolve({
          errors,
          file,
          name: file.name.replace(/\s/g, '_').toLowerCase(),
          src: e.target?.result ?? '',
          type,
        });
      };

      reader.onerror = () => {
        reject({
          errors: [
            ...(errors || []),
            {
              message: `Falló la lectura del archivo ${file.name.replace(/\s/g, '_').toLowerCase()}`,
              code: 'file-reader',
            },
          ],
          file,
          name: file.name.replace(/\s/g, '_').toLowerCase(),
          src: '',
          type,
        });
      };

      reader.readAsDataURL(file);
    });
  });

  return Promise.allSettled(promises).then((promiseSettledResult) => {
    const success: FileData[] = [];
    const error: FileData[] = [];

    promiseSettledResult.forEach((fileResult) => {
      if (fileResult.status === 'fulfilled') {
        success.push(fileResult.value);
      } else {
        error.push(fileResult.reason);
      }
    });

    return success.concat(error);
  });
}
